import { announce, h, replace } from "../dom.js";

export interface OrderItem {
  key: string;
  name: string;
  url: string;
}

export interface OrderEditor {
  element: HTMLElement;
  slides(): string[];
  extras(): string[];
}

/**
 * Carousel order, three equivalent ways (docs/WEB_DEPOSIT.md §Interface):
 * drag the handle with a finger or the mouse (pointer events), or the
 * « plus tôt » / « plus tard » / « retirer » buttons, which work with the
 * keyboard and screen readers. Every move is announced. Images taken out stay
 * in the deposit, outside the carousel order.
 */
export function orderEditor(options: { slides: OrderItem[]; extras: OrderItem[]; maxSlides: number; onChange?: () => void }): OrderEditor {
  const items = new Map([...options.slides, ...options.extras].map((i) => [i.key, i]));
  let slides = options.slides.map((i) => i.key);
  let extras = options.extras.map((i) => i.key);
  const element = h("div", { class: "order-editor" });

  const changed = (message: string, focus?: { key: string; action: string }) => {
    render();
    announce(message);
    options.onChange?.();
    if (focus) {
      const target = element.querySelector<HTMLElement>(`[data-key="${focus.key}"] [data-action="${focus.action}"]:not([disabled])`) ?? element.querySelector<HTMLElement>(`[data-key="${focus.key}"] .handle`);
      target?.focus();
    }
  };

  const move = (key: string, delta: number) => {
    const from = slides.indexOf(key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= slides.length) return;
    slides.splice(to, 0, ...slides.splice(from, 1));
    changed(`${items.get(key)!.name} : position ${to + 1} sur ${slides.length}.`, { key, action: delta < 0 ? "earlier" : "later" });
  };

  const remove = (key: string) => {
    if (slides.length <= 1) return;
    slides = slides.filter((k) => k !== key);
    extras = [...extras, key];
    changed(`${items.get(key)!.name} retirée du carrousel, gardée dans le dépôt.`);
  };

  const restore = (key: string) => {
    if (slides.length >= options.maxSlides) {
      announce(`Le carrousel compte déjà ${options.maxSlides} slides.`);
      return;
    }
    extras = extras.filter((k) => k !== key);
    slides = [...slides, key];
    changed(`${items.get(key)!.name} ajoutée en position ${slides.length}.`, { key, action: "earlier" });
  };

  function slideCard(key: string, index: number): HTMLElement {
    const item = items.get(key)!;
    const handle = h("button", { class: "handle", type: "button", "aria-label": `Glisser pour déplacer ${item.name}`, title: "Glisser pour déplacer" }, "⠿");
    const card = h(
      "li",
      { class: "slide", "data-key": key },
      h("div", { class: "cover" }, h("img", { src: item.url, alt: `Slide ${index + 1} : ${item.name}`, loading: "lazy", draggable: "false" })),
      h("span", { class: "pos", "aria-hidden": "true" }, String(index + 1)),
      handle,
      h(
        "div",
        { class: "meta" },
        h("div", { class: "name", title: item.name }, item.name),
        h(
          "div",
          { class: "tools" },
          h("button", { class: "icon-btn", type: "button", "data-action": "earlier", "aria-label": `Placer ${item.name} plus tôt`, title: "Plus tôt", disabled: index === 0, onclick: () => move(key, -1) }, "←"),
          h("button", { class: "icon-btn", type: "button", "data-action": "later", "aria-label": `Placer ${item.name} plus tard`, title: "Plus tard", disabled: index === slides.length - 1, onclick: () => move(key, 1) }, "→"),
          h("span", { class: "spacer" }),
          h("button", { class: "text-btn", type: "button", "data-action": "remove", disabled: slides.length <= 1, onclick: () => remove(key) }, "Retirer"),
        ),
      ),
    );
    dragWith(handle, card);
    return card;
  }

  /**
   * Pointer drag (finger, pen or mouse) on the handle: the card follows the pointer's
   * position in the grid. Moves are followed on the window, not through pointer capture:
   * moving the card in the DOM would release a capture held by its handle.
   */
  function dragWith(handle: HTMLElement, card: HTMLElement): void {
    handle.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      const list = card.parentElement!;
      const pointer = event.pointerId;
      card.classList.add("dragging");
      // near the top or bottom edge, the page scrolls so that every position can be reached
      let last = { x: event.clientX, y: event.clientY };
      let scrolling = 0;
      const autoScroll = () => {
        const edge = 72;
        const step = last.y > window.innerHeight - edge ? 14 : last.y < edge ? -14 : 0;
        if (step) {
          window.scrollBy(0, step);
          place(last.x, last.y);
        }
        scrolling = requestAnimationFrame(autoScroll);
      };
      scrolling = requestAnimationFrame(autoScroll);
      const onMove = (e: PointerEvent) => {
        if (e.pointerId !== pointer) return;
        e.preventDefault();
        last = { x: e.clientX, y: e.clientY };
        place(e.clientX, e.clientY);
      };
      const place = (x: number, y: number) => {
        const others = [...list.children].filter((c) => c !== card) as HTMLElement[];
        let before: HTMLElement | null = null;
        for (const other of others) {
          const r = other.getBoundingClientRect();
          const sameRow = y >= r.top && y <= r.bottom;
          if ((sameRow && x < r.left + r.width / 2) || y < r.top) {
            before = other;
            break;
          }
        }
        if (before !== card.nextElementSibling) list.insertBefore(card, before);
      };
      const onUp = (e: PointerEvent) => {
        if (e.pointerId !== pointer) return;
        cancelAnimationFrame(scrolling);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        card.classList.remove("dragging");
        const order = [...list.children].map((c) => (c as HTMLElement).dataset["key"]!);
        const key = card.dataset["key"]!;
        if (order.join() !== slides.join()) {
          slides = order;
          changed(`${items.get(key)!.name} : position ${slides.indexOf(key) + 1} sur ${slides.length}.`);
        } else render();
      };
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    });
  }

  function render(): void {
    replace(
      element,
      h("ol", { class: "slides", "aria-label": `Ordre du carrousel, ${slides.length} slides` }, slides.map(slideCard)),
      extras.length
        ? h(
            "section",
            { class: "extras" },
            h("h2", null, "Hors carrousel"),
            h("p", { class: "muted" }, "Gardées dans le dépôt, sans position. Ajoutez-les si elles font partie du carrousel."),
            h(
              "ul",
              { class: "slides compact" },
              extras.map((key) => {
                const item = items.get(key)!;
                return h(
                  "li",
                  { class: "slide", "data-key": key },
                  h("div", { class: "cover" }, h("img", { src: item.url, alt: item.name, loading: "lazy" })),
                  h(
                    "div",
                    { class: "meta" },
                    h("div", { class: "name", title: item.name }, item.name),
                    h("button", { class: "btn", type: "button", "data-action": "add", disabled: slides.length >= options.maxSlides, onclick: () => restore(key) }, "Ajouter au carrousel"),
                  ),
                );
              }),
            ),
          )
        : null,
    );
  }

  render();
  return { element, slides: () => [...slides], extras: () => [...extras] };
}
